import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Container,
  Link,
  CircularProgress,
} from "@mui/material";
import { getVisibleVoteInfo } from "../../apis/user-apis/VoteInfoApis";

const iconMap = [
  { keywords: ["location", "site", "place", "where", "polling"], icon: "📍" },
  { keywords: ["date", "schedule", "when"], icon: "📅" },
  { keywords: ["register", "registration", "check"], icon: "📋" },
  { keywords: ["way", "how", "method", "absentee", "mail"], icon: "🗳️" },
  { keywords: ["primary", "election day"], icon: "🏛️" },
  { keywords: ["id", "requirement", "need"], icon: "✅" },
];

const fallbackIcons = ["📋", "📅", "🗳️", "✅", "📍", "✉️", "👤", "🏛️"];

const getIconForTitle = (title, index) => {
  const lower = (title || "").toLowerCase();
  const match = iconMap.find((entry) =>
    entry.keywords.some((kw) => lower.includes(kw)),
  );
  return match ? match.icon : fallbackIcons[index % fallbackIcons.length];
};

const VotingSection = () => {
  const [votingInfo, setVotingInfo] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchVoteInfo = async () => {
      try {
        setLoading(true);
        const data = await getVisibleVoteInfo();
        if (data.success) {
          setVotingInfo(data.boxes || []);
        } else {
          setError(data.error || "Failed to load voting info");
        }
      } catch (err) {
        console.error("Error fetching vote info:", err);
        setError("Unable to connect to the server.");
      } finally {
        setLoading(false);
      }
    };

    fetchVoteInfo();
  }, []);

  if (!loading && votingInfo.length === 0) {
    return null;
  }

  return (
    <Box
      className="voting-section-root"
      sx={{ bgcolor: "#1a263e", py: { xs: 8, md: 10 }, width: "100%" }}
    >
      <Container maxWidth="lg" className="voting-container">
        {/* HEADER SECTION */}
        <Box className="voting-header" sx={{ mb: 6 }}>
          <Typography
            className="overline-text"
            sx={{
              color: "#C59324",
              fontWeight: 700,
              letterSpacing: "0.2em",
              fontSize: "0.85rem",
              textTransform: "uppercase",
              mb: 2,
            }}
          >
            Your Vote
          </Typography>

          <Typography
            className="headline-main"
            sx={{
              mb: "8px",
              color: "#fff",
              fontWeight: 700,
              fontFamily: "'Playfair Display', serif",
              mt: 1,
              fontSize: {
                xs: "2rem",
                sm: "2.5rem",
                md: "3rem",
              },
            }}
          >
            Make Sure{" "}
            <Box
              component="span"
              sx={{
                color: "#C59324",
                fontFamily: "'Playfair Display', serif",
                fontStyle: "italic",
                fontWeight: 500,
              }}
            >
              Your Voice is Heard
            </Box>
          </Typography>

          <Typography
            className="header-subtext"
            sx={{
              color: "rgba(255, 255, 255, 0.6)",
              fontSize: "0.95rem",
              lineHeight: 1.6,
              maxWidth: "600px",
            }}
          >
            Key dates, registration links, and everything you need to vote in
            the 2026 Delaware elections.
          </Typography>
        </Box>

        {/* INFO CARDS FLEX WRAPPER */}
        <Box
          className="voting-cards-wrapper"
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: "repeat(4, 1fr)",
            },
            gap: { xs: 1.5, sm: 2 },
            minHeight: "100px",
          }}
        >
          {loading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gridColumn: "1 / -1",
                py: 5,
              }}
            >
              <CircularProgress sx={{ color: "#C59324" }} />
            </Box>
          ) : error ? (
            <Box sx={{ gridColumn: "1 / -1", textAlign: "center", py: 5 }}>
              <Typography sx={{ color: "rgba(255,255,255,0.6)" }}>
                {error}
              </Typography>
            </Box>
          ) : (
            votingInfo.map((info, index) => (
              <Box
                key={info._id || index}
                className={`voting-card-item-${index}`}
                sx={{
                  minHeight: { xs: "auto", sm: "170px" },
                  borderRadius: "8px",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  bgcolor: "rgba(255, 255, 255, 0.03)",
                  p: 3,
                  textAlign: "center",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    bgcolor: "rgba(255, 255, 255, 0.06)",
                    borderColor: "rgba(255, 255, 255, 0.2)",
                  },
                }}
              >
                {/* ICON */}
                <Typography sx={{ fontSize: "2rem", mb: 2 }}>
                  {getIconForTitle(info.title, index)}
                </Typography>

                {/* CARD CONTENT */}
                <Typography
                  className="card-title"
                  sx={{
                    color: "#FFF",
                    fontSize: "1rem",
                    fontWeight: 800,
                    mb: 1,
                  }}
                >
                  {info.title}
                </Typography>

                <Typography
                  className="card-desc"
                  sx={{
                    color: "rgba(255, 255, 255, 0.7)",
                    fontSize: "0.85rem",
                    lineHeight: 1.5,
                    mb: 2,
                    flexGrow: 1,
                  }}
                >
                  {info.body || info.desc}
                </Typography>

                <Link
                  className="card-link"
                  href={info.linkUrl || info.url}
                  target="_blank"
                  sx={{
                    color: "#C59324",
                    fontWeight: 700,
                    fontSize: "0.8rem",
                    textDecoration: "none",
                    transition: "0.3s",
                    "&:hover": { opacity: 0.8 },
                  }}
                >
                  {info.linkText}
                </Link>
              </Box>
            ))
          )}
        </Box>
      </Container>
    </Box>
  );
};

export default VotingSection;
